import { ContentfulImage } from '@components/contentful-image'
import { Markdown } from '@components/markdown'
import { ActionButton } from '@contentful-components/action-button'
import type { BentoPieceSquareFragment } from '@graphql/queries/GetBentoPieceSquare.graphql'
import { optimizedBgUrl } from '@lib/utils/contentful-image'
import { isGradientColor } from '@lib/utils/isGradientColor'
import variables, { type Variable } from '@styles/variables'
import type { FunctionComponent } from 'react'

import styles from './bento-square.module.scss'

export const BentoSquare: FunctionComponent<Props> = ({
    layout = 'text',
    justifyContent = 'flex-start',
    headingMd = '',
    headingColor = 'white',
    subtitleMd = '',
    button,
    copyImage,
    copyImageLocation = 'top',
    backgroundType = 'color',
    backgroundColor = 'darkGray',
    backgroundImage = null,
    backgroundVideo = null,
    image = null,
    alignImage = 'center',
    isMarketplace = false,
}) => {
    function getBackgroundStyle() {
        if (backgroundType === 'video') {
            return { minHeight: '300px' }
        }
        if (backgroundType === 'image') {
            return { backgroundImage: getBackgroundImageUrl() }
        }
        if (isGradientColor(backgroundColor)) {
            return { background: variables[backgroundColor as Variable] }
        }

        return { backgroundColor: variables[backgroundColor as Variable] }
    }

    function getBackgroundImageUrl() {
        return optimizedBgUrl(backgroundImage?.url, 800)
    }

    function getHeadingStyle() {
        if (headingColor === 'gradient') return
        return {
            color: headingColor,
        }
    }

    return (
        <div
            className={`${styles.boxOne} ${
                isMarketplace ? styles.marketplace : ''
            }`}
            style={getBackgroundStyle()}
        >
            <div className={styles.boxOneContent}>
                {backgroundVideo && backgroundType === 'video' && (
                    <div className={styles.videoContainer}>
                        <video
                            aria-label='Background video'
                            className={styles.backgroundVideo}
                            autoPlay
                            muted
                            playsInline
                            loop={backgroundVideo.loop}
                            poster={backgroundVideo.poster?.source}
                        >
                            <source
                                src={backgroundVideo?.files?.items[0].source}
                                type='video/mp4'
                            />
                        </video>
                    </div>
                )}

                {layout === 'text' && (
                    <div
                        className={styles.boxOneCopy}
                        style={{ justifyContent: justifyContent }}
                    >
                        {copyImage && copyImageLocation === 'top' && (
                            <div className={styles.copyImage}>
                                <ContentfulImage
                                    url={copyImage?.url}
                                    width={copyImage?.width}
                                    height={copyImage?.height}
                                    alt={copyImage?.alt}
                                    contentType={copyImage?.contentType}
                                    sizes='(max-width: 767px) calc(100vw - 40px), (max-width: 1023px) 220px, 440px'
                                />
                            </div>
                        )}

                        {isMarketplace && (
                            <div className={styles.marketplaceSpacer} />
                        )}

                        <div
                            className={`${styles.heading} ${
                                headingColor === 'gradient'
                                    ? styles.headinggradient
                                    : ''
                            }`}
                            style={getHeadingStyle()}
                        >
                            <Markdown
                                tag='p'
                                typeStyle={{
                                    _: 'heading-xs',
                                    md: 'heading-md',
                                    lg: 'heading-lg',
                                }}
                                markdown={headingMd}
                                color={headingColor}
                            />
                        </div>

                        <div className={styles.subtitle}>
                            <Markdown
                                tag='p'
                                typeStyle={{ _: 'body-lg', md: 'body-md' }}
                                markdown={subtitleMd}
                            />
                        </div>

                        {copyImage && copyImageLocation === 'bottom' && (
                            <div style={{ marginBottom: '24px' }}>
                                <ContentfulImage
                                    url={copyImage?.url}
                                    width={copyImage?.width}
                                    height={copyImage?.height}
                                    alt={copyImage?.alt}
                                    contentType={copyImage?.contentType}
                                    sizes='(max-width: 767px) calc(100vw - 40px), (max-width: 1023px) 220px, 440px'
                                />
                            </div>
                        )}

                        <div className={styles.buttons}>
                            {!!button && (
                                <ActionButton
                                    preset={button?.preset}
                                    {...button}
                                    isMarketplace={isMarketplace}
                                />
                            )}
                        </div>
                    </div>
                )}

                {layout === 'image' && image && (
                    <div className={styles.media}>
                        <div
                            className={styles.imageContainer}
                            style={{ alignItems: alignImage }}
                        >
                            <div className={styles.boxOneImage}>
                                {image && (
                                    <ContentfulImage
                                        className={styles.image}
                                        url={image?.url}
                                        width={image?.width}
                                        height={image?.height}
                                        alt={image?.alt}
                                        contentType={image?.contentType}
                                        sizes='(max-width: 767px) calc(100vw - 40px), (max-width: 1023px) 220px, 440px'
                                        style={{ width: '100%' }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default BentoSquare

type Props = Omit<BentoPieceSquareFragment, 'sys' | 'type'> & {
    index?: number
    layout?: string | undefined
}
